import { useCallback, useRef } from 'react';

import { useTypedSelector } from '../../../shared/hooks';
import { useGetViolationsGraphCusterMutation } from '../data-layer/violationsTabApi';
import { getListPayload } from '../utils';

const useGetGraphCluster = () => {
  const { tabInAction, activeFilters, searchKey, sortInAction, activeInsight } =
    useTypedSelector((state) => state.aag.violationsTab.controls);

  const { activeFilters: entitlementActiveFilters } = useTypedSelector(
    (state) => state.aag.details.entitlementViolationsTab
  );

  // Add entitlement context from entitlement violations tab state
  const isEntitlementContext = window.location.pathname.includes('entitlement');
  const getActiveFilters =
    isEntitlementContext && entitlementActiveFilters.length > 0
      ? entitlementActiveFilters
      : activeFilters;
  const abortRef = useRef<((reason: string) => void) | null>(null);

  const [fetchGraphCluster, { isLoading, isError }] =
    useGetViolationsGraphCusterMutation();

  const handleFetchGraphCluster = useCallback(
    async (clusterId: string) => {
      abortRef.current?.('New Search');

      const activeFiltersMap = Object.fromEntries(
        getActiveFilters.map((f) => [f.value, f.options.map((o) => o.value)])
      );
      const payload = getListPayload(
        activeFiltersMap,
        tabInAction ?? undefined,
        searchKey,
        sortInAction,
        activeInsight,
        { max: 50, offset: 0 }
      );

      try {
        const controller = fetchGraphCluster({
          clusterId,
          payload: {
            filters: payload.filters,
            pagination: payload.pagination,
          },
        });

        abortRef.current = () => controller.abort();

        const response = await controller.unwrap();

        return response;
      } catch {
        throw Error('Failed fetching graph cluster');
      }
    },
    [
      fetchGraphCluster,
      tabInAction,
      searchKey,
      sortInAction,
      activeInsight,
      getActiveFilters,
    ]
  );

  return {
    handleFetchGraphCluster,
    isLoading,
    isError,
  };
};

// eslint-disable-next-line import/prefer-default-export
export { useGetGraphCluster };
